<template>
  <div
    class="bookingDashboard bookingDashboard--list customEmptyLegsAndSpacesList"
    :class="multipleSelect ? 'customEmptyLegsAndSpacesList--with-checkbox' : 'customEmptyLegsAndSpacesList--without-checkbox'"
  >
    <div
      v-if="loading"
      class="customEmptyLegsAndSpacesDashboard__empty"
    >
      Loading...
    </div>
    <div
      v-else-if="!records.length"
      class="customEmptyLegsAndSpacesDashboard__empty"
    >
      {{ emptyMessage }}
    </div>
    <div
      v-else
      class="bookingDashboard__table"
    >
      <div class="bookingDashboard__table-header">
        <div v-if="multipleSelect" class="bookingDashboard__table-cell bookingDashboard__table-cell--checkbox">
          <label class="customEmptyLegsAndSpacesList__checkbox">
            <input
              type="checkbox"
              :checked="allSelected"
              @change="toggleSelectAll($event.target.checked)"
            />
            <span class="customEmptyLegsAndSpacesList__checkbox-mark"></span>
          </label>
        </div>
        <div class="bookingDashboard__table-cell">Aircraft Model</div>
        <div class="bookingDashboard__table-cell">Departure</div>
        <div class="bookingDashboard__table-cell">Arrival</div>
        <div class="bookingDashboard__table-cell">Price</div>
        <div class="bookingDashboard__table-cell">Date</div>
      </div>
      <div class="bookingDashboard__table-body">
        <vue-scroll :ops="ops">
          <div
            v-for="item of records"
            :key="item.emptyLegId || item.id"
            class="bookingDashboard__table-row"
          >
            <div v-if="multipleSelect" class="bookingDashboard__table-cell bookingDashboard__table-cell--checkbox">
              <label class="customEmptyLegsAndSpacesList__checkbox">
                <input
                  type="checkbox"
                  :checked="isSelected(item)"
                  @change="toggleRecord(item, $event.target.checked)"
                />
                <span class="customEmptyLegsAndSpacesList__checkbox-mark"></span>
              </label>
            </div>
            <div class="bookingDashboard__table-cell aircraftNumber">{{ item.aircraftModelClass }}</div>
            <div class="bookingDashboard__table-cell aircraftModel">{{ item.departure }}</div>
            <div class="bookingDashboard__table-cell aircraftModel">{{ item.arrival }}</div>
            <div class="bookingDashboard__table-cell price">{{ formatPrice(item) }}</div>
            <div class="bookingDashboard__table-cell aircraftType">
              {{ formatDate(item.departureDate) }}
              <router-link
                class="bookingDashboard__table-detail"
                :to="`/admin/custom-empty-legs-and-spaces/${recordId(item)}`"
              >
                click for details
              </router-link>
              <div
                class="fleetDashboard__list-item-remove js-remove-aircraft"
                @click="showRemoveModal(item)"
              >
                <SvgIcon name="close"/>
              </div>
            </div>
          </div>
        </vue-scroll>
      </div>
    </div>
    <QuestionPopup
      :name="'questionPopupCustomEmptyLeg'"
      :title="'The empty leg will be removed'"
      :text="'Are you sure?'"
      @remove="removeRecord"
    />
  </div>
</template>

<script>
  import moment from 'moment'
  import { aircraftsAPI } from '@/api/aircrafts'
  import SvgIcon from '@/components/common/SvgIcon/SvgIcon'
  import QuestionPopup from '@/components/common/modals/QuestionPopup'
  import '@/views/dashboard/common/Fleet/Fleet.styl'
  import './CustomEmptyLegsAndSpacesList.styl'

  export default {
    name: 'CustomEmptyLegsAndSpacesList',
    props: {
      historical: {
        type: Boolean,
        default: false,
      },
      multipleSelect: {
        type: Boolean,
        default: false,
      },
    },
    data: () => ({
      loading: false,
      removing: false,
      pendingRemoveId: null,
      selectedIds: [],
      ops: {
        rail: {
          size: '3px',
        },
        bar: {
          size: '3px',
          background: 'rgba(0,0,0,0.1)',
        },
      },
    }),
    computed: {
      records () {
        const today = moment().startOf('day')

        return this.$store.state.availabilities.flightAvailabilities.filter((item) => {
          const departureDate = moment(item.departureDate).startOf('day')

          return this.historical
            ? departureDate.isBefore(today)
            : !departureDate.isBefore(today)
        })
      },
      emptyMessage () {
        return this.historical
          ? 'No historical custom empty legs and spaces yet.'
          : 'No custom empty legs and spaces yet.'
      },
      allSelected () {
        return this.records.length > 0 && this.selectedIds.length === this.records.length
      },
    },
    watch: {
      $route (to, from) {
        const wasForm = from?.path?.match(/custom-empty-legs-and-spaces\/(new|[^/]+)$/)
        const isList = to.path.endsWith('custom-empty-legs-and-spaces')

        if (wasForm && isList) {
          this.fetchRecords()
        }
      },
      records () {
        this.selectedIds = this.selectedIds.filter(id => this.records.some(item => this.recordId(item) === id))
        this.emitSelection()
      },
    },
    created () {
      this.fetchRecords()
    },
    methods: {
      recordId (item) {
        return item.emptyLegId || item.id
      },
      isSelected (item) {
        return this.selectedIds.includes(this.recordId(item))
      },
      toggleRecord (item, checked) {
        const id = this.recordId(item)

        if (checked && !this.selectedIds.includes(id)) {
          this.selectedIds.push(id)
        }

        if (!checked) {
          this.selectedIds = this.selectedIds.filter(itemId => itemId !== id)
        }

        this.emitSelection()
      },
      toggleSelectAll (checked) {
        this.selectedIds = checked ? this.records.map(item => this.recordId(item)) : []
        this.emitSelection()
      },
      emitSelection () {
        if (!this.multipleSelect) return

        this.$emit('selection-change', this.records.filter(item => this.selectedIds.includes(this.recordId(item))))
      },
      formatDate (date) {
        return date ? moment(date).format('MMM DD, YYYY') : ''
      },
      formatPrice (item) {
        if (item.exclusiveCost == null) return ''

        return item.exclusiveCost.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
      },
      showRemoveModal (item) {
        this.pendingRemoveId = this.recordId(item)
        this.$modal.show('questionPopupCustomEmptyLeg')
      },
      async removeRecord () {
        if (!this.pendingRemoveId || this.removing) return

        this.removing = true

        try {
          await aircraftsAPI.removeEmptyLegs({ emptyLegId: this.pendingRemoveId })
          this.pendingRemoveId = null
          this.selectedIds = []
          this.emitSelection()
          await this.fetchRecords()
        } catch (e) {
          console.log(e)
        }

        this.removing = false
      },
      async fetchRecords () {
        this.loading = true

        try {
          await this.$store.dispatch('getFlightAvailabilities')
        } catch (e) {
          console.log(e)
        }

        this.loading = false
      },
    },
    components: {
      SvgIcon,
      QuestionPopup,
    },
  }
</script>
